import { captureException } from "@web-monorepo/telemetry";

const subscribers = new Map<string, Set<() => void>>();
const versions = new Map<string, number>();
let isListeningToStorageEvents = false;

function getKey(type: string, key: string): string {
  return `${type}:${key}`;
}

function incrementVersion(type: string, key: string): void {
  const subscriberKey = getKey(type, key);
  versions.set(subscriberKey, (versions.get(subscriberKey) ?? 0) + 1);
}

export function notifyAll(type: string): void {
  for (const subscriberKey of subscribers.keys()) {
    if (!subscriberKey.startsWith(`${type}:`)) {
      continue;
    }

    const key = subscriberKey.slice(type.length + 1);
    incrementVersion(type, key);

    const callbackSet = subscribers.get(subscriberKey);
    if (!callbackSet) {
      continue;
    }

    for (const callback of callbackSet) {
      try {
        callback();
        // eslint-disable-next-line no-catch-all/no-catch-all
      } catch (error) {
        captureException(error);
      }
    }
  }
}

function handleStorageEvent(event: StorageEvent): void {
  // storage events only fire for localStorage changes from other tabs
  if (event.key === null) {
    // storage.clear() was called - notify all localStorage subscribers
    notifyAll("local");
    return;
  }

  // Notify subscribers for this specific key
  notify("local", event.key);
}

function ensureStorageEventListener(): void {
  if (isListeningToStorageEvents || typeof window === "undefined") {
    return;
  }
  isListeningToStorageEvents = true;
  window.addEventListener("storage", handleStorageEvent);
}

export function subscribe(type: string, key: string, callback: () => void): () => void {
  ensureStorageEventListener();

  const subscriberKey = getKey(type, key);
  let callbackSet = subscribers.get(subscriberKey);
  if (!callbackSet) {
    callbackSet = new Set();
    subscribers.set(subscriberKey, callbackSet);
  }
  callbackSet.add(callback);

  return () => {
    const callbackSet = subscribers.get(subscriberKey);
    callbackSet?.delete(callback);
    if (callbackSet?.size === 0) {
      subscribers.delete(subscriberKey);
    }
  };
}

export function notify(type: string, key: string): void {
  incrementVersion(type, key);

  const callbackSet = subscribers.get(getKey(type, key));
  if (callbackSet) {
    for (const callback of callbackSet) {
      try {
        callback();
        // eslint-disable-next-line no-catch-all/no-catch-all
      } catch (error) {
        captureException(error);
      }
    }
  }
}

export function getVersion(type: string, key: string): number {
  return versions.get(getKey(type, key)) ?? 0;
}
